import { Skeleton } from "@/components/kit";
import { formatQtyWithUnit } from "@/modules/products/domain/unit-conversion";
import type { SalesFrequencyRowDTO } from "@/modules/reports/domain/types";
import { formatDateLocal } from "@/shared/lib/date-time";
import {
  ReportMobileCard,
  ReportProductLink,
  ReportStat,
} from "./ReportProductLink";

type SalesFrequencyTableProps = {
  rows?: SalesFrequencyRowDTO[];
  isLoading?: boolean;
  embedded?: boolean;
};

function formatAvgDays(days: number | null): string {
  if (days == null) return "—";
  if (days >= 30) {
    const months = Math.floor(days / 30);
    const remain = Math.round(days % 30);
    return remain > 0 ? `~${months} ເດືອນ ${remain} ວັນ` : `~${months} ເດືອນ`;
  }
  return `~${days} ວັນ`;
}

function formatDaysSince(days: number): string {
  if (days === 0) return "ມື້ນີ້";
  if (days === 1) return "1 ວັນ";
  return `${days} ວັນ`;
}

function daysSinceClass(days: number): string {
  return days >= 14 ? "text-destructive" : "text-muted-foreground";
}

export function SalesFrequencyTable({
  rows = [],
  isLoading,
  embedded = false,
}: SalesFrequencyTableProps) {
  return (
    <div className="brand-card overflow-hidden">
      {embedded ? null : (
        <div className="border-secondary/15 border-b px-4 py-3 sm:px-5">
          <h2 className="font-display text-lg">ຄວາມຖີ່ການຂາຍ</h2>
          <p className="text-muted-foreground text-sm">
            ຈຳນວນຄັ້ງທີ່ຖືກສັ່ງ, ຄວາມຖີ່ຕໍ່ອາທິດ ແລະ ຂາຍຄັ້ງລ່າສຸດ — ຕໍ່ variant
          </p>
        </div>
      )}
      {isLoading ? (
        <div className="flex flex-col gap-2 p-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={`freq-skeleton-${i}`} className="h-10 rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <p className="p-6 text-center text-muted-foreground text-sm">
          ບໍ່ມີຂໍ້ມູນໃນຊ່ວງທີ່ເລືອກ
        </p>
      ) : (
        <>
          <div className="flex flex-col gap-3 p-4 md:hidden">
            {rows.map((row) => (
              <ReportMobileCard key={row.variantId}>
                <ReportProductLink
                  productId={row.productId}
                  productName={row.productName}
                  variantName={row.variantName}
                />
                <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <ReportStat label="ຄັ້ງ" value={row.orderCount} />
                  <ReportStat
                    label="ຊິ້ນ"
                    value={
                      <>
                        {formatQtyWithUnit(row.quantity, row.stockUnitLabel)}
                        {row.bonusQuantity > 0 ? (
                          <span className="block text-muted-foreground text-xs">
                            +
                            {formatQtyWithUnit(
                              row.bonusQuantity,
                              row.stockUnitLabel,
                            )}{" "}
                            ແຖມ
                          </span>
                        ) : null}
                      </>
                    }
                  />
                  <ReportStat
                    label="ຄັ້ງ/ອາທິດ"
                    value={row.ordersPerWeek.toFixed(1)}
                  />
                  <ReportStat
                    label="ລະຫວ່າງຄັ້ງ"
                    value={formatAvgDays(row.avgDaysBetweenOrders)}
                    valueClassName="text-muted-foreground"
                  />
                  <ReportStat
                    label="ຂາຍລ່າສຸດ"
                    value={formatDateLocal(row.lastSoldAt)}
                  />
                  <ReportStat
                    label="ເງົຍາ"
                    value={formatDaysSince(row.daysSinceLastSale)}
                    valueClassName={daysSinceClass(row.daysSinceLastSale)}
                  />
                </dl>
              </ReportMobileCard>
            ))}
          </div>

          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-secondary/10 border-b text-left text-muted-foreground">
                  <th className="px-4 py-2 font-medium sm:px-5">ສິນຄ້າ</th>
                  <th className="px-4 py-2 text-right font-medium sm:px-5">
                    ຄັ້ງ
                  </th>
                  <th className="px-4 py-2 text-right font-medium sm:px-5">
                    ຊິ້ນ
                  </th>
                  <th className="px-4 py-2 text-right font-medium sm:px-5">
                    ຄັ້ງ/ອາທິດ
                  </th>
                  <th className="px-4 py-2 text-right font-medium sm:px-5">
                    ລະຫວ່າງຄັ້ງ
                  </th>
                  <th className="px-4 py-2 font-medium sm:px-5">ຂາຍລ່າສຸດ</th>
                  <th className="px-4 py-2 text-right font-medium sm:px-5">
                    ເງົຍາ
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.variantId}
                    className="border-secondary/5 border-b last:border-0"
                  >
                    <td className="px-4 py-2.5 sm:px-5">
                      <ReportProductLink
                        productId={row.productId}
                        productName={row.productName}
                        variantName={row.variantName}
                      />
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums sm:px-5">
                      {row.orderCount}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums sm:px-5">
                      {formatQtyWithUnit(row.quantity, row.stockUnitLabel)}
                      {row.bonusQuantity > 0 ? (
                        <div className="text-muted-foreground text-xs">
                          +
                          {formatQtyWithUnit(
                            row.bonusQuantity,
                            row.stockUnitLabel,
                          )}{" "}
                          ແຖມ
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums sm:px-5">
                      {row.ordersPerWeek.toFixed(1)}
                    </td>
                    <td className="px-4 py-2.5 text-right text-muted-foreground tabular-nums sm:px-5">
                      {formatAvgDays(row.avgDaysBetweenOrders)}
                    </td>
                    <td className="px-4 py-2.5 sm:px-5">
                      {formatDateLocal(row.lastSoldAt)}
                    </td>
                    <td
                      className={`px-4 py-2.5 text-right tabular-nums sm:px-5 ${daysSinceClass(row.daysSinceLastSale)}`}
                    >
                      {formatDaysSince(row.daysSinceLastSale)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
